import React, { useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { MapPin, Star, X } from '../icons/StatboxIcons'
import { stripPlusCodeFromAddress } from '../../lib/venuesApi'

/**
 * Confirmed-venue display card.
 *
 * Used by SetupWizardLocations under the autocomplete. The card itself
 * is the framer-motion mount/exit unit so AnimatePresence in the parent
 * can choreograph stack additions/removals. A short scale pulse runs
 * whenever `pulseToken` changes — the parent uses that to draw attention
 * to an existing card when the user re-selects the same venue.
 *
 * Props:
 *   - venue {object}  Full venue record from draft.setupWizard.venues.
 *   - onRemove {() => void}
 *   - pulseToken {number}
 *       Bumped by the parent to trigger the duplicate-attention pulse.
 */
export default function VenueCard({ venue, onRemove, pulseToken = 0 }) {
  const reduceMotion = useReducedMotion()
  const isManual = !!venue.isManual

  return (
    <motion.div
      layout={reduceMotion ? false : 'position'}
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={
        reduceMotion
          ? { opacity: 1, y: 0, scale: 1 }
          : pulseToken > 0
          ? {
              opacity: 1,
              y: 0,
              scale: [1, 1.02, 1],
              transition: { duration: 0.2, ease: [0.16, 1, 0.3, 1] },
            }
          : { opacity: 1, y: 0, scale: 1 }
      }
      exit={
        reduceMotion
          ? { opacity: 0, transition: { duration: 0 } }
          : { opacity: 0, scale: 0.96, transition: { duration: 0.2 } }
      }
      transition={
        reduceMotion
          ? { duration: 0 }
          : { duration: 0.3, ease: [0.16, 1, 0.3, 1] }
      }
      role="listitem"
      className="relative rounded-xl border border-[var(--signup-border-subtle)] bg-[var(--signup-bg-elevated)] p-4"
      style={{ boxShadow: 'var(--signup-shadow-sm)' }}
    >
      <div className="flex items-start gap-3">
        <VenuePhoto venue={venue} isManual={isManual} />

        <div className="min-w-0 flex-1 pr-9">
          <div className="text-[15px] font-medium text-[var(--signup-text-primary)] leading-tight">
            {venue.name}
          </div>
          <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[13px] text-[var(--signup-text-secondary)]">
            <MapPin
              size={13}
              className="shrink-0 text-[var(--signup-accent)]"
              strokeWidth={2.25}
              aria-hidden
            />
            <span className="truncate">
              {isManual
                ? 'Added manually'
                : stripPlusCodeFromAddress(venue.formattedAddress)}
            </span>
          </div>
          {!isManual && typeof venue.rating === 'number' && (
            <div className="mt-1 inline-flex items-center gap-1 text-[13px] text-[var(--signup-text-secondary)]">
              <Star size={12} fill="#f59e0b" stroke="#f59e0b" aria-hidden />
              <span>{venue.rating.toFixed(1)}</span>
              <span className="text-[var(--signup-text-tertiary)]">
                · {venue.ratingCount} reviews
              </span>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${venue.name}`}
          className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--signup-text-tertiary)] hover:text-[var(--signup-text-primary)] hover:bg-[var(--signup-bg-subtle)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--signup-accent)] motion-safe:transition-colors motion-safe:duration-150"
        >
          <X size={16} aria-hidden />
        </button>
      </div>
    </motion.div>
  )
}

function VenuePhoto({ venue, isManual }) {
  const [errored, setErrored] = useState(false)
  const showPlaceholder = isManual || !venue.photoUrl || errored
  return (
    <div
      className={`relative h-[60px] w-[60px] shrink-0 overflow-hidden rounded-lg ${
        showPlaceholder
          ? 'bg-[var(--signup-accent-subtle)]'
          : 'bg-[var(--signup-bg-subtle)]'
      }`}
      aria-hidden
    >
      {!showPlaceholder ? (
        <img
          src={venue.photoUrl}
          alt=""
          loading="lazy"
          onError={() => setErrored(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-[var(--signup-accent-subtle)]">
          <MapPin
            size={22}
            className="text-[var(--signup-accent)]"
            strokeWidth={2.25}
            aria-hidden
          />
        </div>
      )}
    </div>
  )
}
